import { DisconnectOutlined, LinkOutlined } from '@ant-design/icons';
import { type Editor } from '@tiptap/react';
import { Form, Input, Popconfirm, Space } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { StyledForm } from '../../../../form/StyledForm.tsx';
import { RichTextButton } from '../RichTextMenuButton.tsx';
import { RichTextMenuDivider } from '../RichTextMenuDivider.tsx';

const StyletPopconfirm = styled(Popconfirm)`
  .ant-popconfirm-message-title {
    margin-inline-start: 0;
  }
`;

export interface IRichTextLinkButtonsProps {
  editor: Editor | null;
}

/**
 * Pulsanti dell'editor Rich Text per la gestione dei link
 */
export function RichTextLinkButtons(props: IRichTextLinkButtonsProps) {
  const { editor } = props;
  const { t } = useTranslation();
  const [openPopover, setOpenPopover] = useState(false);
  const [urlValue, setUrlValue] = useState('');

  const setLink = useCallback(() => {
    if (!editor || urlValue === null) return;

    // Se il valore è vuoto disattiviamo il link nell'editor
    if (urlValue === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    // Altrimenti aggiorniamo il link nell'editor
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: urlValue, target: '_blank' })
      .run();

    setOpenPopover(false);
  }, [editor, urlValue]);

  const showPopconfirm = () => {
    if (!editor) return;
    // All'aperura del Popconfirm,
    // recupero l'eventuale link già presente nell'editor
    const previousUrl = editor.getAttributes('link').href || '';
    // e lo setto come valore di default del campo input
    setUrlValue(previousUrl);

    setOpenPopover(true);
  };

  const handleCancel = () => {
    setOpenPopover(false);
    // Se si Annulla l'operazione del Popconfirm,
    // Resetto il valore del campo input
    setTimeout(() => {
      setUrlValue('');
    }, 300);
  };

  if (!editor) return null;

  const onChangeUrl = (e: any) => {
    // Aggiorno il link con il valore del campo input
    setUrlValue(e.target.value);
  };

  return (
    <Space size={2}>
      <RichTextMenuDivider />
      {/** Link */}
      <StyletPopconfirm
        placement="topLeft"
        align={{ offset: [-10, 4] }}
        icon={null}
        title={
          <StyledForm>
            <Form.Item
              label={t('inputs.rich-text-link-button.label', 'URL')}
              style={{ width: 500, margin: ' -8px 0 4px -8px' }}
            >
              <Input
                style={{ width: '100%' }}
                size="large"
                onChange={onChangeUrl}
                value={urlValue}
                placeholder={t(
                  'inputs.rich-text-link-button.placeholder',
                  "Inserisci l'URL del link",
                )}
              />
            </Form.Item>
          </StyledForm>
        }
        open={openPopover}
        onConfirm={setLink}
        onCancel={handleCancel}
      >
        <RichTextButton
          icon={<LinkOutlined />}
          isActive={!!editor.isActive('link')}
          title="Link"
          onClick={showPopconfirm}
          disabled={!editor.isEditable}
        />
      </StyletPopconfirm>

      {/** Unlink */}
      <RichTextButton
        icon={<DisconnectOutlined />}
        title="Link"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isEditable || !editor.isActive('link')}
      />
    </Space>
  );
}
